<template>
  <div>
    <Banner type="order" />
    <div class="page-container j-container-box-shadow container">
      <div class="title">
        <div class="left-section">
          <div class="back" @click="handleBack">
            <BackArrowIcon class="icon" />
            <span class="icon-label">{{ $t('order.back') }}</span>
          </div>

          <a-divider type="vertical" style="margin: 0 30px; height: 28px" />

          <div class="order-title">
            {{ $t('order.part_logistics_title') }}
          </div>
        </div>

        <div class="action-section" @click="handleDownload">
          <DownloadIcon class="icon" />
          <span class="action-label">{{ $t('order.download') }}</span>
        </div>
      </div>

      <a-divider style="margin: 30px 0" />

      <div class="info">
        <a-row>
          <a-col :xs="24" :sm="24" :md="8">
            <a-descriptions :column="1" :colon="false">
              <a-descriptions-item :label="$t('order.bl_no')">
                {{ detail.logisticsNum }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('order.delivery_method')">
                {{ detail.deliveryMethod }}
              </a-descriptions-item>
              <a-descriptions-item
                class="j-custom-description-item"
                :label="$t('order.remark')"
              >
                {{ detail.remark }}
              </a-descriptions-item>
            </a-descriptions>
          </a-col>
          <a-col :xs="24" :sm="24" :md="8">
            <a-descriptions :column="1" :colon="false">
              <a-descriptions-item :label="$t('order.quantity')">
                {{ detail.totalQuantity }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('order.etd')">
                {{ detail.etd ? moment(detail.etd).format('YYYY-MM-DD') : '' }}
              </a-descriptions-item>
            </a-descriptions>
          </a-col>

          <a-col :xs="24" :sm="24" :md="8">
            <a-descriptions :column="1" :colon="false">
              <a-descriptions-item :label="$t('order.status')">
                {{
                  detail.logisticsStatus !== null && detail.logisticsStatus >= 0
                    ? $t(`logistics_status.${detail.logisticsStatus}`)
                    : ''
                }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('order.eta')">
                {{ detail.eta ? moment(detail.eta).format('YYYY-MM-DD') : '' }}
              </a-descriptions-item>
            </a-descriptions>
          </a-col>
        </a-row>
      </div>

      <a-divider style="margin: 30px 0" />

      <div class="batch">
        <a-row>
          <a-col
            :xs="24"
            :sm="24"
            :md="12"
            v-for="(b, index) in batches"
            :key="index"
          >
            <a-descriptions :column="2">
              <a-descriptions-item>
                <template #label>
                  <span class="checkbox">
                    <a-checkbox
                      @change="handleCheckChange"
                      v-model:checked="b.checked"
                    />
                  </span>
                  <span>{{ $t('order.order_no') }}</span>
                </template>
                <span>{{ b.orderNum }}</span>
              </a-descriptions-item>
              <a-descriptions-item :label="$t('order.batch')">
                {{ b.batch ? $t(b.batch) : '' }}
              </a-descriptions-item>
            </a-descriptions>
          </a-col>
        </a-row>
      </div>

      <div class="table-container">
        <a-table
          :pagination="ipagination"
          :columns="columns"
          size="middle"
          row-key="id"
          :loading="loading"
          :data-source="dataSource"
        >
          <template #name="{text}">
            {{ text ? $t(text) : '' }}
          </template>

          <template #groupName="{text}">
            {{ text ? $t(text) : '' }}
          </template>

          <template #unit="{text}">
            {{ text ? $t(text) : '' }}
          </template>

          <template #unitPrice="{text}">
            {{ text !== null ? numeral(text || 0).format('$0,0.00') : '' }}
          </template>

          <template #totalPrice="{text}">
            {{ text !== null ? numeral(text || 0).format('$0,0.00') : '' }}
          </template>

          <template #[`${col}-title`] v-for="col in colTitle" :key="col">
            {{ $t(`order.${col}`) }}
          </template>
        </a-table>

        <div class="pagination-container">
          <a-pagination
            v-model:current="ipagination.current"
            :page-size-options="ipagination.pageSizeOptions"
            :total="ipagination.total"
            show-size-changer
            v-model:pageSize="ipagination.pageSize"
            @showSizeChange="onShowSizeChange"
            @change="onChange"
            size="small"
            show-quick-jumper
          >
            <template #buildOptionText="props">
              <span
                >{{ props.value }} {{ $t('common.page_per_item_text') }}</span
              >
            </template>
          </a-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import moment from 'moment';
import numeral from 'numeral';
import Banner from '@/components/banner/Banner.vue';
import BackArrowIcon from '@/components/icons/BackArrowIcon.vue';
import DownloadIcon from '@/components/icons/DownloadIcon.vue';
import {
  getLogisticsRelList,
  getLogisticsDetail,
  getLogisticsBatchList
} from '@/api/apis';
import { downloadFile } from '@/api/request';
import { baseUrl } from '@/utils/constants';

interface PartLogisticsDetailProps {
  dataSource: any[];
  batches: any[];
  orderNums: any[];
  detail: any;
  [key: string]: any;
}

export default defineComponent({
  name: 'PartLogisticsDetail',
  components: {
    Banner,
    BackArrowIcon,
    DownloadIcon
  },
  data(): PartLogisticsDetailProps {
    return {
      numeral,
      moment,
      loading: false,
      colTitle: [
        'part_type',
        'part_name',
        'part_no',
        'part_code',
        'unit',
        'unit_price',
        'total_price',
        'quantity',
        'remark'
      ],
      columns: [
        {
          dataIndex: 'groupName',
          width: 120,
          slots: { title: 'part_type-title', customRender: 'groupName' }
        },
        {
          dataIndex: 'name',
          width: 100,
          slots: { customRender: 'name', title: 'part_name-title' }
        },
        {
          dataIndex: 'modelNum',
          width: 100,
          slots: { title: 'part_no-title' }
        },
        {
          dataIndex: 'partCode',
          width: 100,
          slots: { title: 'part_code-title' }
        },
        {
          dataIndex: 'unit',
          width: 100,
          slots: { customRender: 'unit', title: 'unit-title' }
        },
        {
          dataIndex: 'deliverQuantity',
          width: 100,
          slots: { customRender: 'quantity', title: 'quantity-title' }
        },
        {
          dataIndex: 'sellingPrice',
          width: 100,
          slots: { customRender: 'unitPrice', title: 'unit_price-title' }
        },
        {
          dataIndex: 'totalPrice',
          width: 100,
          slots: { customRender: 'totalPrice', title: 'total_price-title' }
        },
        {
          title: this.$t('order.remark'),
          width: 100,
          dataIndex: 'remark',
          slots: { title: 'remark-title' }
        }
      ],
      dataSource: [],
      batches: [],
      ipagination: {
        current: 1,
        pageSizeOptions: ['5', '10', '15'],
        total: 6,
        showSizeChanger: true,
        pageSize: 5,
        showQuickJumper: true
        // size: 'large'
      },
      id: 0,
      orderNums: [],
      detail: {}
    };
  },
  created() {
    const id = +(this.$route.params.id as string);

    if (!isNaN(id) && id > 0) {
      this.id = id;

      this.loadData();
    }
  },
  computed: {
    ...mapState(['lang'])
  },
  methods: {
    async handleDownload() {
      await downloadFile(
        `${baseUrl}ureport/excel?_u=db:user_parts_logistics_detail_excel.ureport.xml&logisticsId=${this.id}&locale=${this.lang}`,
        this.$t('order.part_logistics_title') + '.xlsx',
        null
      );
    },
    async loadData() {
      const res = await Promise.all([
        getLogisticsDetail({ id: this.id }),
        getLogisticsRelList({ logisticsId: this.id })
      ]);

      if (res && res.length === 2 && res.every(s => s.success)) {
        this.detail = res[0].result || {};

        this.batches = res[1].result || [];
      }

      this.orderNums = this.batches.map(s => s.orderNum);

      await this.loadGridData();
    },
    async loadGridData() {
      this.loading = true;

      const res = await getLogisticsBatchList({
        pageNo: this.ipagination.current,
        pageSize: this.ipagination.pageSize,
        logisticsId: this.id,
        orderNums: this.orderNums.join(',')
      });

      if (res.success) {
        this.ipagination.total = res.result.total;
        this.dataSource = res.result.records || [];
      }

      this.loading = false;
    },
    onShowSizeChange(current) {
      this.ipagination.current = current;
      this.loadGridData();
    },
    onChange(page, pageSize) {
      this.ipagination.current = page;
      this.ipagination.pageSize = pageSize;
      this.loadGridData();
    },
    handleCheckChange() {
      if (this.batches.length === this.batches.filter(s => !s.checked).length) {
        this.orderNums = this.batches.map(s => s.orderNum);
      } else {
        this.orderNums = this.batches
          .filter(s => s.checked)
          .map(s => s.orderNum);
      }

      this.loadGridData();
    },
    handleBack() {
      // this.$router.push('/order?tab=3');
      this.$router.back();
    }
  }
});
</script>

<style lang="scss" scoped>
.container {
  padding: 0 49px;
  background-color: white;

  .info {
    margin-bottom: 30px;
  }

  .title {
    padding: 36px 0 0 0;

    display: flex;
    justify-content: space-between;
    align-items: center;

    .left-section {
      display: flex;
      align-items: center;

      .order-title {
        color: $primary-color;
        font-weight: bold;
        font-size: $secondary-fontsize;
      }

      .back {
        display: flex;
        align-items: center;

        cursor: pointer;

        .icon-label {
          margin-left: 10px;
          font-size: $content-fontsize;
          color: #999999;
        }
      }
    }

    .action-section {
      display: flex;
      align-items: center;
      cursor: pointer;
      color: $primary-color;
      .action-label {
        margin-left: 10px;
      }

      &:hover {
        color: $primary-hover-color;
      }
    }
  }

  .table-container {
    margin-bottom: 40px;
  }

  .batch {
    margin-bottom: 30px;

    .checkbox {
      margin-right: 20px;
    }
  }
}

.pagination-container {
  margin: 20px 0;
  text-align: right;
}

::v-deep(.ant-descriptions-item-label) {
  color: #999999;
}

.info ::v-deep(.ant-col) {
  &:nth-child(1) .ant-descriptions-item-label {
    min-width: 160px;
  }
  &:nth-child(2) .ant-descriptions-item-label {
    min-width: 100px;
  }
  &:nth-child(3) .ant-descriptions-item-label {
    min-width: 100px;
  }
}

.pagination-container {
  margin: 20px 0;
  text-align: right;
}

// 隐藏table自带的pagination
::v-deep(.ant-table-pagination) {
  display: none;
}
</style>
